import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import SendIcon from '@mui/icons-material/Send'
import { Box, Button, Grid, Stack, Tab, Tabs, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import TerminatedAPI from 'api/faber/TerminatedAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import StatContainer from 'common/AbstractStatLine/StatContainer'
import ColorBox from 'common/Box/ColorWhiteBox'
import TableSkeleton from 'common/Skeletons/TableSkeleton'
import NormalText from 'common/Typography/NormalText'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import SendEmailToAllTerminatedCustomersButton from 'pages/Customers/vendor/Faber/components/TerminatedEmailButton'
import TerminatedPageActionMenu from 'pages/Customers/vendor/Faber/components/TerminatedPageActionMenu'
import UploadTerminatedCSV from 'pages/Customers/vendor/Faber/components/UploadTerminatedCSV'
import {
  getAllExTelcoUsersDetailsType,
  terminatedUsersRowsType
} from 'types/api/faber/terminated.type'

function tabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

export default function FaberTerminatedCustomersMainDashboard() {
  const faberToken = OrbitSession.get('faberToken')

  const [, setRows] = useState<terminatedUsersRowsType[]>([])
  const [pendingRows, setPendingRows] = useState<terminatedUsersRowsType[]>([])
  const [sentRows, setSentRows] = useState<terminatedUsersRowsType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [page, setPage] = useState(0)

  const [pageSize, setPageSize] = useState<number>(1)
  const [pageOffset, setPageOffset] = useState(0)
  const [rowCount, setRowCount] = useState(0)

  const [tabValue, setTabValue] = React.useState(0)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const dataGridTypographySX = {
    color: 'black',
    fontSize: '12px'
  }

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset)
    setPage(newPage)
  }

  const role: string = OrbitSession.get('role')
  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize
    }),
    [pageOffset, pageSize]
  )

  function filterPending(obj) {
    if (obj.emailSent === 0) {
      return true
    } else {
      return false
    }
  }

  function filterEmailSent(obj) {
    if (obj.emailSent !== 0) {
      return true
    } else {
      return false
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      minWidth: 100,
      flex: 1.5,
      renderCell: (params: { row: getAllExTelcoUsersDetailsType }) => {
        return (
          <AbstractDataGridTooltip>{`${params.row.firstName} ${params.row.lastName}`}</AbstractDataGridTooltip>
        )
      }
    },
    {
      field: 'userEmail',
      headerName: 'Email',
      minWidth: 150,
      flex: 1.5,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        return (
          <AbstractDataGridTooltip>
            {params.row.userEmail}
          </AbstractDataGridTooltip>
        )
      }
    },

    {
      field: 'siNumber',
      headerName: 'SIN',
      minWidth: 100,
      flex: 1.5,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        return (
          <AbstractDataGridTooltip>
            {params.row.siNumber}
          </AbstractDataGridTooltip>
        )
      }
    },
    {
      field: 'oldNumber',
      headerName: 'old Number',
      minWidth: 100,
      flex: 1.5,
      renderCell: (params: { row: getAllExTelcoUsersDetailsType }) => {
        return (
          <AbstractDataGridTooltip>
            {`+${params.row.oldCountryCode} ${params.row.oldNumber}`}
          </AbstractDataGridTooltip>
        )
      }
    },
    {
      field: 'newNumber',
      headerName: 'new Number',
      minWidth: 100,
      flex: 1.5,
      renderCell: (params: { row: getAllExTelcoUsersDetailsType }) => {
        if (params.row.newNumber === '') {
          return ''
        } else {
          return (
            <AbstractDataGridTooltip>
              {`+${params.row.newCountryCode} ${params.row.newNumber}`}
            </AbstractDataGridTooltip>
          )
        }
      }
    },
    {
      field: 'cardUpdated',
      headerName: 'Payment Details',
      flex: 1,
      minWidth: 50,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        if (params.row.cardUpdated) {
          return <Typography sx={dataGridTypographySX}>Updated</Typography>
        } else {
          return <Typography sx={dataGridTypographySX}>Not updated</Typography>
        }
      }
    },
    {
      field: 'emailSentTime',
      headerName: 'Latest email sent on',
      align: 'center',
      flex: 1,
      minWidth: 70,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        if (params.row.emailSentTime.length > 0) {
          return params.row.emailSentTime[
            params.row.emailSentTime.length - 1
          ].split(' ')[0]
        } else {
          return 'Nil'
        }
      }
    },

    {
      field: 'emailTtl',
      headerName: 'Email valid till',
      align: 'center',
      flex: 1,
      minWidth: 20,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        const date = new Date(params.row.email_ttl)
        return (
          <Typography sx={dataGridTypographySX}>
            {format(date, 'dd MMM yyyy')}
          </Typography>
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Send Email',
      align: 'center',
      flex: 1,
      minWidth: 100,
      renderCell: (params: { row: terminatedUsersRowsType }) => {
        return (
          <>
            {role === 'opsowner' ? (
              <TerminatedPageActionMenu
                customerRow={params.row}
                refetchAllusers={refetchAllusers}
              />
            ) : (
              <Button
                sx={{
                  backgroundColor: circlesOrbitColorPalette.primary.disabled,
                  borderColor: circlesOrbitColorPalette.text.light,
                  borderWidth: 0.5,
                  ':hover': {
                    backgroundColor: circlesOrbitColorPalette.primary.dark
                  }
                }}
                onClick={() => {
                  toast.error(
                    ' Access denied. You do not have enough permission to execute this action.'
                  )
                }}
              >
                <SendIcon
                  sx={{ color: circlesOrbitColorPalette.neutrals[0] }}
                />
              </Button>
            )}
          </>
        )
      }
    }
  ]

  const { refetch: refetchAllusers } = useQuery(
    ['getAllUsers', [queryOptions, faberToken]],
    async () => {
      return TerminatedAPI.getAllExTelcoUsers(
        queryOptions.pageSize,
        queryOptions.pageOffset,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        const pendingEmails = res.data.body.data.filter(filterPending)
        const sentEmails = res.data.body.data.filter(filterEmailSent)
        setPendingRows(pendingEmails)
        setSentRows(sentEmails)
        setRowCount(res.data.body.total)
        setPageSize(res.data.body.total)
      },
      onError: () => {
        toast.error('Error retrieving users')
        setRows([])
        setIsLoading(false)
      }
    }
  )
  const { data: getTerminatedUserStats, refetch: refetchTerminatedUserStats } =
    useQuery(
      ['getVoucherStats'],
      async () => {
        return TerminatedAPI.getTerminatedUserStats(faberToken)
      },
      {
        onError: () => {
          toast.error('Error getting terminated user total')
        }
      }
    )
  const dataArray = [
    {
      title: 'Total Terminated-Telco Faber Users',
      info: getTerminatedUserStats?.data.data.totalCount
    }
  ]
  useEffect(() => {
    refetchTerminatedUserStats()
    refetchAllusers()
  }, [])

  useEffect(() => {
    refetchAllusers()
  }, [page])

  useEffect(() => {
    refetchAllusers()
  }, [])

  return !isLoading ? (
    <>
      <Grid
        container
        direction='column'
        justifyContent='flex-start'
        alignItems='stretch'
        spacing={2}
        sx={{ padding: 2 }}
      >
        <Grid item>
          <Stack flexDirection={'row'} sx={{ justifyContent: 'space-between' }}>
            <Stack>
              <Typography variant='h4'>Terminated Telco Customers</Typography>
              <NormalText theme='muted'>
                This page is for Biz Ops to help monitor and win back Jetpac
                customers who have terminated their lines with Circles.Life
              </NormalText>
            </Stack>
            {role === 'opsowner' && (
              <UploadTerminatedCSV
                refetchAllUsers={refetchAllusers}
                refetchTerminatedUserStats={refetchTerminatedUserStats}
              />
            )}
          </Stack>
        </Grid>

        <>
          <Box
            sx={{
              display: 'flex',
              rowGap: '12px',
              flexDirection: 'column',
              marginTop: '12px'
            }}
          >
            <StatContainer dataArray={dataArray} />
          </Box>
          <ColorBox sx={{ mt: '20px' }}>
            <Box>
              <Box>
                <Tabs
                  value={tabValue}
                  onChange={handleChange}
                  aria-label='basic tabs example'
                >
                  <Tab label='Pending Email' {...tabProps(0)} />
                  <Tab label='Email Sent' {...tabProps(1)} />
                </Tabs>
              </Box>

              <MUIDataGrid
                getRowId={(row) => `${row.hashUserId}`}
                tableHeight='725px'
                columns={columns}
                rows={tabValue === 0 ? pendingRows : sentRows}
                paginationMode='server'
                rowCount={rowCount}
                pageSize={pageSize}
                page={page}
                onPageChange={handlePageChange}
                components={{ Toolbar: Toolbar }}
                componentsProps={{
                  toolbar: {
                    buttons:
                      role === 'opsowner' && tabValue === 0 ? (
                        <SendEmailToAllTerminatedCustomersButton
                          refetchAllUsers={refetchAllusers}
                        />
                      ) : null
                  }
                }}
              />
            </Box>
          </ColorBox>
        </>
      </Grid>
    </>
  ) : (
    <TableSkeleton />
  )
}
